<script lang="ts">
	//Svelte hooks
	import { beforeUpdate, onMount } from 'svelte';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Services
	import { postReq, localReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { gen1CloudReq } from 'src/services/gen_1_reqs';
	//Helpers
	import {
		get_local_ip,
		get_mode,
		real_id,
		get_gen_2_component_from_category,
		is_gen_2,
		get_status_settings_component_path,
		get_device_component_index,
	} from '../../../helpers/interface_helpers';
	import {
		capFirstChar,
		percent_from_value_range,
		value_from_percent_range,
	} from '../../../helpers/utilities';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	//Configs
	import { devices_config } from '@devices/index';
	//Design system components
	import { Slider, SliderV2 } from '@Shelly/shelly-design-system';
	// Types
	import type { SliderPropsTypes } from '@Shelly/shelly-design-system';
	//Components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { cloud_disabled_devices } from 'src/stores/device-runtime/firmware-ui';
	import { hasControlRights } from 'src/helpers/user_helpers';

	export let device,
		status,
		settings,
		prop: string,
		type: SliderPropsTypes['type'] = 'vertical',
		min = 0,
		max = 100,
		needsRanging = false;

	let loading = false;

	let value = 0;
	let targetValue;
	let valueCache;

	let localIp = false;
	let pinLocked;
	let showModal = false;

	let callbackValue;

	const deviceType = device.type;

	const isGen2 = is_gen_2(device);

	const _comp_path = get_status_settings_component_path(device) || '';

	$: hasRights = hasControlRights(device);

	// for white channel we show the value in %, but send it in range [0-255]

	onMount(() => {
		let mode = get_mode(device, status, settings);
		let slider_type = devices_config[deviceType]?.modes[mode].slider;
		min = devices_config[deviceType]?.modes[mode][slider_type];
	});

	$: isMoving =
		typeof value !== 'undefined' && typeof targetValue !== 'undefined' && value !== valueCache;

	$: if (
		!isMoving ||
		(typeof targetValue === 'undefined' &&
			(isGen2
				? status?.[_comp_path]?.[prop] !== targetValue
				: status[_comp_path][device.channel]?.[prop] !== targetValue))
	) {
		valueFromStatus();
	}

	$: if (
		isGen2
			? status?.[_comp_path]?.[prop] === targetValue && targetValue === value
			: status[_comp_path][device.channel]?.[prop] === targetValue && targetValue === value
	) {
		targetValue = undefined;
		valueCache = undefined;
	}

	const valueFromStatus = () => {
		if (!isGen2 && device.category !== 'sensor' && typeof status[_comp_path] !== 'undefined') {
			value = status[_comp_path][device.channel]?.[prop];

			// ison = status[device.category.split('s_')[0]+'s'][device.channel].ison
		} else if (isGen2) {
			value = status[_comp_path]?.[prop];
		}
		if (needsRanging) {
			value = percent_from_value_range(value, [0, 255]);
		}
		targetValue = value;
	};

	beforeUpdate(() => {
		localIp = get_local_ip(device.id);

		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
	});

	const handleRequest = async (e) => {
		if (!pinLocked) {
			let res;
			// value = val;
			// value = e.detail.value;
			targetValue = e.detail.value;
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}

			let isok;
			loading = true;
			const _value = needsRanging ? value_from_percent_range(targetValue, [0, 255]) : targetValue;

			// update status to look cool :D
			if (isGen2) {
				status[_comp_path][prop] = _value;
				res = await gen2Request(
					device,
					`${get_gen_2_component_from_category(device)}.set`,

					{
						id: get_device_component_index(device),
						[prop]: _value,
					},
				);
			} else {
				status[_comp_path][device.channel][prop] = _value;

				if (localIp) {
					const url = `${localIp}/${device.category}/${device.channel}`;
					let data = {
						[prop]: _value,
					};
					// include turn: on/off parameter for gen1 bulbs #976
					if (prop === 'brightness') {
						data = {
							...data,
							turn: status[_comp_path][device.channel]['ison'] ? 'on' : 'off',
						};
					}
					res = await localReq(device, url, 'post', data, 9000, !isGen2);
					// add error handle here
					isok = res.hasOwnProperty('ison');

					if (!res.isok && typeof $cloud_disabled_devices[device.id] === 'undefined') {
						res = await postReq(`/device/${device.category}/control`, data);
					}
				} else {
					let props = {
						[prop]: _value,
					};
					if (prop === 'brightness') {
						props = {
							...props,
							turn: status[_comp_path][device.channel]['ison'] ? 'on' : 'off',
						};
					}
					res = await gen1CloudReq(device, device.category, props);
				}
			}

			if (res.isok) {
				devices_status.update_device_status({
					id: real_id(device.id),
					msg: status,
				});
			}
			targetValue = undefined;
			loading = false;
		} else {
			callbackValue = value;
			showModal = true;
		}
	};

	const prop_i18n = (prop) => {
		switch (prop) {
			case 'brightness':
			case 'gain':
				return $format('label_brightness');
			case 'white':
				return $format('label_white_channel');
			default:
				return $format(`label_${prop}`);
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleRequest(callbackValue);
		callbackValue = undefined;
	};

	const handleClose = () => {
		valueFromStatus();
	};

	const a11yProps = {
		sliderAriaLabel: $format('control_slider', { default: 'Control slider' }),
		ariaLabel: $format('control_slider', { default: 'Control slider' }),
		ariaLabelValue: $format('control_slider', { default: 'Control slider' }),
	};
</script>

{#if $showNewLayout}
	<SliderV2
		id="slider-tcwbwzk"
		class={type === 'vertical' ? 'flex-full-height mb-l mt-l' : ' mb-m'}
		{a11yProps}
		title={capFirstChar(prop_i18n(prop))}
		bind:value
		{type}
		{min}
		{max}
		on:value_change={handleRequest}
		isLoading={loading}
		disabled={!hasRights}
	/>
{:else}
	<Slider
		id="slider-tcwbwzk"
		class={type === 'vertical' ? 'flex-full-height mb-l' : ' mb-m'}
		{a11yProps}
		title={capFirstChar(prop_i18n(prop))}
		bind:value
		{type}
		{min}
		{max}
		on:value_change={handleRequest}
		isLoading={loading}
		disabled={!hasRights}
	/>
{/if}

{#if showModal}
	<PinUnlock
		bind:showModal
		on:unlock={handlePin}
		on:close={handleClose}
		on:cancel={() => (showModal = false)}
	/>
{/if}
